import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PropTypes from "prop-types";
import React, { useCallback, useMemo } from "react";
import { ButtonCompo } from "../../../components/Comman/Button";
import { LabelCompo } from "../../../components/Comman/Label";
import { TextFieldCompo } from "../../../components/Comman/TextFieldCompo";
import TypographyCompo from "../../../components/Comman/TypographyCompo";
import { titleColor } from "../../../config/ColorObj";
import offerImg from "../../../Images/offer.png";
import BillingPreviewAppliedOfferChip from "./BillingPreviewAppliedOfferChip";
import BillingPreviewBillSummary from "./BillingPreviewBillSummary";
import BillingPreviewProductTable from "./BillingPreviewProductTable";

const BillingPreviewBillCard = ({
	billData,
	index,
	totalBills,
	showOfferIcon,
	showOfferHintNote,
	offerHintFading,
	isShowBillDiscountAndCD,
	showDiscountAmtEditable,
	isDetailedBillingView,
	formErrors,
	onOfferClick,
	onOfferDetailClick,
	onChangeItemDiscPer,
	onChangeItemDiscAmt,
	onChangeBillCd,
}) => {
	const outletName =
		billData.outletBillsAfterApplyingOffers?.outLet?.firmName || "";
	const billNo = billData.outletBillsAfterApplyingOffers?.billNo || "";
	const billHeaderId = billData.outletBillsAfterApplyingOffers?.id;
	const appliedOffersOptimize =
		billData.outletBillsAfterApplyingOffers?.appliedOffersOptimize || [];

	const handleOfferIconClick = useCallback(() => {
		onOfferClick(billHeaderId);
	}, [onOfferClick, billHeaderId]);

	const handleBillCdChange = useMemo(
		() =>
			onChangeBillCd(
				billHeaderId,
				"discountOnBillPer",
				billHeaderId,
			),
		[onChangeBillCd, billHeaderId],
	);

	return (
		<div className="billing-preview-card">
			<div className="billing-preview-card-header">
				<div className="billing-preview-card-header-left">
					<div className="billing-preview-card-icon">
						<ReceiptLongOutlinedIcon fontSize="small" />
					</div>
					<div>
						<TypographyCompo className="billing-preview-card-title">
							{outletName || `Outlet ${index + 1}`}
						</TypographyCompo>
						{billNo && (
							<TypographyCompo className="billing-preview-card-subtitle">
								Bill No. {billNo}
							</TypographyCompo>
						)}
					</div>
				</div>
				{(totalBills > 1 || showOfferIcon) && (
					<div className="billing-preview-card-header-right">
						{showOfferIcon && (
							<ButtonCompo
								type="button"
								variant="text"
								className={
									"billing-preview-offer-icon-btn" +
									(showOfferHintNote
										? " billing-preview-offer-icon-btn--attention"
										: "")
								}
								name={
									<img
										src={offerImg}
										alt="Offer"
										className="billing-preview-offer-icon-img"
									/>
								}
								onClick={handleOfferIconClick}
							/>
						)}
						{totalBills > 1 && (
							<TypographyCompo className="billing-preview-card-badge">
								Bill {index + 1} of {totalBills}
							</TypographyCompo>
						)}
					</div>
				)}
			</div>

			{showOfferIcon && showOfferHintNote && (
				<div
					className={
						"billing-preview-offer-note" +
						(offerHintFading ? " billing-preview-offer-note--fading" : "")
					}
					role="status"
				>
					<img
						src={offerImg}
						alt=""
						className="billing-preview-offer-note-img"
					/>
					<TypographyCompo
						component="span"
						className="billing-preview-offer-note-text"
					>
						Click on this to change or remove offer
					</TypographyCompo>
				</div>
			)}

			<BillingPreviewBillSummary
				billData={billData}
				index={index}
				isShowBillDiscountAndCD={isShowBillDiscountAndCD}
			/>

			<div className="billing-preview-table-section">
				<BillingPreviewProductTable
					billData={billData}
					isDetailedBillingView={isDetailedBillingView}
					isShowBillDiscountAndCD={isShowBillDiscountAndCD}
					showDiscountAmtEditable={showDiscountAmtEditable}
					onItemDiscPerChange={onChangeItemDiscPer}
					onItemDiscAmtChange={onChangeItemDiscAmt}
				/>

				{isShowBillDiscountAndCD && (
					<div className="billing-preview-cd-bar">
						<div className="billing-preview-cd-label">
							<LabelCompo
								className="text-black"
								style={{ fontSize: 16, color: titleColor }}
								label="CD(%) :"
							/>
						</div>
						<div className="billing-preview-cd-input">
							<TextFieldCompo
								placeHolder={"Enter CD"}
								color="primary"
								name="discountOnBillPer"
								value={
									billData.outletBillsAfterApplyingOffers.discountOnBillPer
								}
								fullWidth
								onChange={handleBillCdChange}
								errorText={
									formErrors["nameError"] ? formErrors["nameError"] : ""
								}
							/>
						</div>
					</div>
				)}

				{Object.keys(billData.outletBillsAfterApplyingOffers).length !== 0 &&
					appliedOffersOptimize.length !== 0 && (
						<div className="billing-preview-offers-section">
							<div className="billing-preview-offers-header">
								<LocalOfferOutlinedIcon className="billing-preview-offers-header-icon" />
								<TypographyCompo className="billing-preview-offers-header-text">
									Applied Offers
								</TypographyCompo>
								<TypographyCompo
									component="span"
									className="billing-preview-offers-count"
								>
									{appliedOffersOptimize.length}
								</TypographyCompo>
							</div>
							<div className="billing-preview-offers-list">
								{appliedOffersOptimize.map((offerAppliedData, offerIndex) => (
									<BillingPreviewAppliedOfferChip
										key={offerAppliedData.id || offerIndex}
										offerId={offerAppliedData.id}
										offerName={offerAppliedData?.name}
										offerIndex={offerIndex}
										onOfferDetailClick={onOfferDetailClick}
									/>
								))}
							</div>
						</div>
					)}
			</div>
		</div>
	);
};

BillingPreviewBillCard.propTypes = {
	billData: PropTypes.object.isRequired,
	index: PropTypes.number.isRequired,
	totalBills: PropTypes.number.isRequired,
	showOfferIcon: PropTypes.bool.isRequired,
	showOfferHintNote: PropTypes.bool.isRequired,
	offerHintFading: PropTypes.bool.isRequired,
	isShowBillDiscountAndCD: PropTypes.bool.isRequired,
	showDiscountAmtEditable: PropTypes.bool.isRequired,
	isDetailedBillingView: PropTypes.bool.isRequired,
	formErrors: PropTypes.object.isRequired,
	onOfferClick: PropTypes.func.isRequired,
	onOfferDetailClick: PropTypes.func.isRequired,
	onChangeItemDiscPer: PropTypes.func.isRequired,
	onChangeItemDiscAmt: PropTypes.func.isRequired,
	onChangeBillCd: PropTypes.func.isRequired,
};

export default React.memo(BillingPreviewBillCard);
